import { useEffect, useState } from "react"
import { type OperationContext, type OperationResult, useMutation, useQuery } from "urql"

import type {
  Classification,
  ClassificationMutationCreateArgs,
  ClassificationMutationUpdateArgs,
  Mutation,
  Query,
} from "@/graphql/codegen/graphql"
import {
  AddClassificationDocument,
  ClassificationListDocument,
  UpdateClassificationDocument,
} from "@/graphql/codegen/graphql"

const LIMIT = 20

interface UseProductClassificationProps {
  query?: string
  pause?: boolean
  categoryIds?: string[]
  classIds?: string[]
}

const useProductClassification = ({
  query = "",
  pause = false,
  categoryIds = [],
  classIds = [],
}: UseProductClassificationProps): {
  productClassList: Classification[]
  productClassListFetching: boolean
  loadMore: () => void
  hasMore: boolean

  createClass: (
    variables: ClassificationMutationCreateArgs,
    context?: Partial<OperationContext>
  ) => Promise<OperationResult<Pick<Mutation, "classification">, ClassificationMutationCreateArgs>>
  updateClass: (
    variables: ClassificationMutationUpdateArgs,
    context?: Partial<OperationContext>
  ) => Promise<OperationResult<Pick<Mutation, "classification">, ClassificationMutationUpdateArgs>>
} => {
  const [offset, setOffset] = useState(0)
  const [productClassList, setProductClassList] = useState<Classification[]>([])
  const [hasMore, setHasMore] = useState<boolean>(false)

  const [{ data, fetching: productClassListFetching }] = useQuery<Pick<Query, "classification">>({
    query: ClassificationListDocument,
    variables: { input: { filter: { query, categoryIds, classIds }, limit: LIMIT, offset } },
    pause,
  })

  useEffect(() => {
    if (!data) return

    const classifications =
      data?.classification?.list?.__typename === "ClassificationListSuccess"
        ? data?.classification?.list?.classifications
        : []

    setProductClassList((prev) => {
      const uniqueIds = new Set(prev.map((classification) => classification.classificationId))
      return offset === 0
        ? classifications
        : [...prev, ...classifications.filter((classification) => !uniqueIds.has(classification.classificationId))]
    })
    setHasMore(classifications.length % LIMIT === 0)
  }, [data, offset])

  const loadMore = () => {
    setOffset((prevOffset) => prevOffset + LIMIT)
  }

  useEffect(() => {
    setOffset(0)
  }, [query])

  // Mutation to create a new classification
  const [, createClass] = useMutation<Pick<Mutation, "classification">, ClassificationMutationCreateArgs>(
    AddClassificationDocument
  )

  // Mutation to update a classification
  const [, updateClass] = useMutation<Pick<Mutation, "classification">, ClassificationMutationUpdateArgs>(
    UpdateClassificationDocument
  )

  return {
    productClassList,
    productClassListFetching,
    loadMore,
    hasMore,
    createClass,
    updateClass,
  }
}

export default useProductClassification
